import { useEffect, useMemo, useState } from "react";

import {
  type ListTransactionsQuery,
  useTransactions,
} from "../../api/hooks/useTransactions";
import { useAccounts } from "../../api/hooks/useAccounts";
import { useCreateTransaction } from "../../api/hooks/useCreateTransaction";
import { useDeleteTransaction } from "../../api/hooks/useDeleteTransaction";
import { useRestoreTransaction } from "../../api/hooks/useRestoreTransaction";
import { useUpdateTransaction } from "../../api/hooks/useUpdateTransaction";
import type { components } from "../../api/schema";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Textarea } from "../ui/textarea";
import { formatMoney } from "../../lib/money";
import { cn } from "../../lib/cn";

type TransactionResponse = components["schemas"]["TransactionResponse"];
type TransactionType = components["schemas"]["TransactionType"];
type AccountResponse = components["schemas"]["AccountResponse"];

const TRANSACTION_TYPES: ReadonlyArray<{ value: TransactionType; label: string }> = [
  { value: "deposit", label: "Deposit" },
  { value: "withdrawal", label: "Withdrawal" },
  { value: "dividend", label: "Dividend" },
];

const TRANSACTION_TYPE_LABEL: Record<TransactionType, string> = {
  deposit: "Deposit",
  withdrawal: "Withdrawal",
  dividend: "Dividend",
};

const DECIMAL_RE = /^-?\d+(\.\d+)?$/;

// List display: render the stored UTC instant in the viewer's LOCAL wall clock
// (no "Z"), so a transaction's shown time matches what the user typed into the
// local datetime-local field and lines up with fill timestamps (which the fill
// form also stores local->UTC via toISOString).
export function formatDateTime(iso: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  const pad = (n: number): string => n.toString().padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())} ${pad(
    dt.getHours(),
  )}:${pad(dt.getMinutes())}`;
}

function safeFormatMoney(amount: string, currency: string): string {
  try {
    return formatMoney(amount, { currency });
  } catch {
    return `${amount} ${currency}`;
  }
}

// Prefill (edit): project the stored UTC instant into the LOCAL wall clock the
// datetime-local input expects, so editing round-trips without shifting the
// time by the viewer's UTC offset.
export function toDateTimeLocalInputValue(iso: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "";
  const pad = (n: number): string => n.toString().padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(
    dt.getDate(),
  )}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

// Write: the HTML datetime-local input yields "YYYY-MM-DDTHH:MM" in the user's
// local wall clock with no zone. Interpret it as LOCAL and convert to UTC for
// the wire, matching the fill form's toISOString(). The former `${value}:00Z`
// mislabeled the local wall clock as UTC, offsetting every transaction from the
// fills by the viewer's UTC offset.
export function fromDateTimeLocalInputValue(value: string): string {
  if (!value) return "";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toISOString();
}

interface TransactionFormState {
  transactionType: TransactionType;
  amount: string;
  currency: string;
  accountId: string;
  occurredAt: string;
  symbol: string;
  note: string;
}

function initialFormState(
  tx: TransactionResponse | null,
  defaultAccountId: string,
): TransactionFormState {
  if (tx === null) {
    return {
      transactionType: "deposit",
      amount: "",
      currency: "USD",
      accountId: defaultAccountId,
      occurredAt: toDateTimeLocalInputValue(new Date().toISOString()),
      symbol: "",
      note: "",
    };
  }
  return {
    transactionType: tx.transaction_type,
    amount: tx.amount_amount,
    currency: tx.amount_currency,
    accountId: tx.account_id,
    occurredAt: toDateTimeLocalInputValue(tx.occurred_at),
    symbol: tx.symbol ?? "",
    note: tx.note ?? "",
  };
}

interface TransactionFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  initialTransaction: TransactionResponse | null;
  accountOptions: ReadonlyArray<AccountResponse>;
}

function TransactionFormDialog({
  open,
  onOpenChange,
  mode,
  initialTransaction,
  accountOptions,
}: TransactionFormDialogProps) {
  const createMutation = useCreateTransaction();
  const updateMutation = useUpdateTransaction();
  const firstAccountId = accountOptions[0]?.account_id ?? "";
  const [form, setForm] = useState<TransactionFormState>(() =>
    initialFormState(initialTransaction, firstAccountId),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initialFormState(initialTransaction, firstAccountId));
      setError(null);
    }
  }, [open, initialTransaction, firstAccountId]);

  function handleClose(): void {
    onOpenChange(false);
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!DECIMAL_RE.test(form.amount) || Number(form.amount) <= 0) {
      setError("Amount must be a positive decimal.");
      return;
    }
    if (form.currency.length !== 3) {
      setError("Currency must be a 3-letter code (e.g. USD).");
      return;
    }
    if (form.accountId === "") {
      setError("Account is required.");
      return;
    }
    if (form.occurredAt === "") {
      setError("Occurred at is required.");
      return;
    }
    const occurredAtIso = fromDateTimeLocalInputValue(form.occurredAt);
    const symbol = form.symbol.trim() === "" ? null : form.symbol.trim();
    const note = form.note.trim() === "" ? null : form.note.trim();
    try {
      if (mode === "create") {
        await createMutation.mutateAsync({
          account_id: form.accountId,
          transaction_type: form.transactionType,
          amount_amount: form.amount,
          amount_currency: form.currency,
          occurred_at: occurredAtIso,
          symbol,
          note,
        });
      } else if (initialTransaction !== null) {
        await updateMutation.mutateAsync({
          transactionId: initialTransaction.transaction_id,
          body: {
            account_id: form.accountId,
            transaction_type: form.transactionType,
            amount_amount: form.amount,
            amount_currency: form.currency,
            occurred_at: occurredAtIso,
            symbol,
            note,
          },
        });
      }
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save the transaction.");
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "New transaction" : "Edit transaction"}
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label>Type</Label>
            <div
              role="radiogroup"
              aria-label="Transaction type"
              className="flex gap-2"
            >
              {TRANSACTION_TYPES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={form.transactionType === option.value}
                  data-testid={`tx-type-radio-${option.value}`}
                  onClick={() =>
                    setForm((prev) => ({ ...prev, transactionType: option.value }))
                  }
                  className={cn(
                    "rounded-md border px-3 py-1 text-sm",
                    form.transactionType === option.value
                      ? "border-primary bg-primary text-on-primary"
                      : "border-outline bg-surface-container text-on-surface hover:bg-surface-container-high",
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="tx-amount">Amount</Label>
              <Input
                id="tx-amount"
                data-testid="tx-amount-input"
                value={form.amount}
                placeholder="100.00"
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, amount: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tx-currency">Currency</Label>
              <Input
                id="tx-currency"
                data-testid="tx-currency-input"
                value={form.currency}
                maxLength={3}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    currency: e.target.value.toUpperCase(),
                  }))
                }
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tx-account">Account</Label>
            <select
              id="tx-account"
              data-testid="tx-account-select"
              value={form.accountId}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, accountId: e.target.value }))
              }
              className="h-9 w-full rounded-md border border-outline bg-surface-container px-3 py-1 text-sm"
            >
              <option value="">Select an account…</option>
              {accountOptions.map((account) => (
                <option key={account.account_id} value={account.account_id}>
                  {account.is_deleted ? `${account.name} (deleted)` : account.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tx-occurred-at">Occurred at (UTC)</Label>
            <Input
              id="tx-occurred-at"
              data-testid="tx-occurred-at-input"
              type="datetime-local"
              value={form.occurredAt}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, occurredAt: e.target.value }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tx-symbol">Symbol (optional)</Label>
            <Input
              id="tx-symbol"
              data-testid="tx-symbol-input"
              value={form.symbol}
              placeholder="AAPL"
              onChange={(e) =>
                setForm((prev) => ({ ...prev, symbol: e.target.value }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tx-note">Note (optional)</Label>
            <Textarea
              id="tx-note"
              data-testid="tx-note-input"
              rows={4}
              value={form.note}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, note: e.target.value }))
              }
            />
          </div>
          {error !== null ? (
            <p role="alert" className="text-sm font-medium text-danger">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {mode === "create" ? "Create transaction" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface DeleteTransactionDialogProps {
  transaction: TransactionResponse | null;
  onOpenChange: (open: boolean) => void;
}

function DeleteTransactionDialog({
  transaction,
  onOpenChange,
}: DeleteTransactionDialogProps) {
  const mutation = useDeleteTransaction();
  const [error, setError] = useState<string | null>(null);
  async function handleConfirm() {
    if (transaction === null) return;
    setError(null);
    try {
      await mutation.mutateAsync({ transactionId: transaction.transaction_id });
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete the transaction.",
      );
    }
  }
  return (
    <Dialog
      open={transaction !== null}
      onOpenChange={(next) => {
        if (!next) setError(null);
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete this transaction?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-on-surface-variant">
          The transaction will be soft-deleted. You can restore it from the
          show-deleted view.
        </p>
        {error !== null ? (
          <p role="alert" className="text-sm font-medium text-danger">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-danger text-on-primary hover:opacity-90"
            disabled={mutation.isPending}
            onClick={handleConfirm}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface TransactionsSectionProps {
  /**
   * When true the create-transaction dialog opens on mount. Drives the
   * Equity Curve "Add transaction" deep-link (`/accounts?newTransaction=1`).
   */
  autoOpenCreate?: boolean;
}

export function TransactionsSection({ autoOpenCreate = false }: TransactionsSectionProps) {
  const [accountFilter, setAccountFilter] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [formDialog, setFormDialog] = useState<
    | { open: false }
    | { open: true; mode: "create" }
    | { open: true; mode: "edit"; transaction: TransactionResponse }
  >(autoOpenCreate ? { open: true, mode: "create" } : { open: false });
  const [deleteTarget, setDeleteTarget] = useState<TransactionResponse | null>(
    null,
  );

  const txListQuery: ListTransactionsQuery = useMemo(() => {
    const base: ListTransactionsQuery = {};
    if (includeDeleted) base.include_deleted = true;
    if (accountFilter !== "") base.account_id = [accountFilter];
    return base;
  }, [accountFilter, includeDeleted]);

  const transactionsQuery = useTransactions(txListQuery);
  const accountsQuery = useAccounts({ include_deleted: true });
  const restoreMutation = useRestoreTransaction();

  const accounts = accountsQuery.data?.items ?? [];
  const activeAccounts = useMemo(
    () => accounts.filter((acct) => !acct.is_deleted),
    [accounts],
  );
  const accountById = useMemo(() => {
    const map = new Map<string, AccountResponse>();
    for (const acct of accounts) {
      map.set(acct.account_id, acct);
    }
    return map;
  }, [accounts]);

  const transactions = transactionsQuery.data?.items ?? [];

  function handleRestore(tx: TransactionResponse): void {
    restoreMutation.mutate({ transactionId: tx.transaction_id });
  }

  return (
    <section data-testid="transactions-section" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-medium tracking-tight text-on-surface">
            Transactions
          </h2>
          <p className="mt-1 text-sm text-on-surface-variant">
            Deposits, withdrawals, and dividends recorded against your accounts.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setFormDialog({ open: true, mode: "create" })}
          disabled={activeAccounts.length === 0}
        >
          Add transaction
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-on-surface">
            Transactions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-on-surface">
              <span>Account</span>
              <select
                data-testid="tx-account-filter"
                value={accountFilter}
                onChange={(e) => setAccountFilter(e.target.value)}
                className="h-9 rounded-md border border-outline bg-surface-container px-3 py-1 text-sm"
              >
                <option value="">All accounts</option>
                {accounts.map((account) => (
                  <option key={account.account_id} value={account.account_id}>
                    {account.is_deleted ? `${account.name} (deleted)` : account.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-on-surface">
              <input
                type="checkbox"
                data-testid="include-deleted-tx-toggle"
                checked={includeDeleted}
                onChange={(e) => setIncludeDeleted(e.target.checked)}
              />
              Show deleted transactions
            </label>
          </div>
          {transactionsQuery.isLoading ? (
            <p role="status" className="text-sm text-on-surface-variant">
              Loading transactions…
            </p>
          ) : transactionsQuery.isError ? (
            <p role="alert" className="text-sm font-medium text-danger">
              {transactionsQuery.error instanceof Error
                ? transactionsQuery.error.message
                : "Failed to load transactions."}
            </p>
          ) : transactions.length === 0 ? (
            <p className="text-sm text-on-surface-variant">
              No transactions match the current filters.
            </p>
          ) : (
            <Table data-testid="transactions-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Occurred (UTC)</TableHead>
                  <TableHead>Symbol</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((tx) => {
                  const accountName = accountById.get(tx.account_id)?.name ?? "—";
                  return (
                    <TableRow
                      key={tx.transaction_id}
                      data-testid={`tx-row-${tx.transaction_id}`}
                      data-deleted={tx.is_deleted ? "true" : undefined}
                      className={cn(tx.is_deleted && "opacity-60")}
                    >
                      <TableCell>{TRANSACTION_TYPE_LABEL[tx.transaction_type]}</TableCell>
                      <TableCell className="text-right">
                        {safeFormatMoney(tx.amount_amount, tx.amount_currency)}
                      </TableCell>
                      <TableCell>{accountName}</TableCell>
                      <TableCell>{formatDateTime(tx.occurred_at)}</TableCell>
                      <TableCell>{tx.symbol ?? "—"}</TableCell>
                      <TableCell className="max-w-[16rem] truncate" title={tx.note ?? ""}>
                        {tx.note ?? "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {tx.is_deleted ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={restoreMutation.isPending}
                            onClick={() => handleRestore(tx)}
                          >
                            Restore
                          </Button>
                        ) : (
                          <div className="flex justify-end gap-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                setFormDialog({
                                  open: true,
                                  mode: "edit",
                                  transaction: tx,
                                })
                              }
                            >
                              Edit
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="text-danger hover:bg-danger-soft"
                              onClick={() => setDeleteTarget(tx)}
                            >
                              Delete
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <TransactionFormDialog
        open={formDialog.open}
        onOpenChange={(open) => {
          if (!open) setFormDialog({ open: false });
        }}
        mode={formDialog.open ? formDialog.mode : "create"}
        initialTransaction={
          formDialog.open && formDialog.mode === "edit"
            ? formDialog.transaction
            : null
        }
        accountOptions={activeAccounts}
      />
      <DeleteTransactionDialog
        transaction={deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      />
    </section>
  );
}
